// useLabSounds — lightweight audio feedback for lab interactions.
//
// Four sounds, all preloaded from /public/sounds/:
//   cmd-right.mp3   — plays after a command runs cleanly (student advancing)
//   cmd-wrong.mp3   — plays when the router returns an IOS error (`% Invalid…`)
//   lab-right.mp3   — plays on grade pass (3 s celebratory)
//   lab-wrong.mp3   — plays on grade fail   (3 s downbeat)
//
// Mute state persists in localStorage and is broadcast via a window-level
// custom event so the mute button in the Objectives panel updates every
// mount that observes it.
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

const STORAGE_KEY = "hypexlabs.sounds.muted";
const CHANGE_EVENT = "hypexlabs:sounds-muted-changed";

const SOUNDS = {
  cmdRight:  "/sounds/cmd-right.mp3",
  cmdWrong:  "/sounds/cmd-wrong.mp3",
  labRight:  "/sounds/lab-right.mp3",
  labWrong:  "/sounds/lab-wrong.mp3",
};

// --- shared mute state (store) ------------------------------------------------
function readMuted() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}
function writeMuted(v) {
  try {
    localStorage.setItem(STORAGE_KEY, v ? "true" : "false");
  } catch {
    /* no-op */
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}
function subscribeMuted(cb) {
  window.addEventListener(CHANGE_EVENT, cb);
  window.addEventListener("storage", cb);
  return () => {
    window.removeEventListener(CHANGE_EVENT, cb);
    window.removeEventListener("storage", cb);
  };
}

// IOS-error signatures we treat as "wrong command".
// Deliberately narrow so we don't false-positive on informational syslog
// (which starts with `%SYS-`, `%LINK-`, `%LINEPROTO-`, etc.).
const IOS_ERROR_RE = /(?:^|\n)\s*%\s+(?:Invalid|Incomplete|Ambiguous|Unknown|Bad|Error|Login\s+invalid)/i;
// Simulator sometimes returns bare "Invalid input detected." without the %.
const BARE_ERROR_RE = /Invalid\s+input\s+detected/i;

export function outputHasIosError(text) {
  if (!text) return false;
  return IOS_ERROR_RE.test(text) || BARE_ERROR_RE.test(text);
}

// --- main hook ---------------------------------------------------------------
export default function useLabSounds() {
  const muted = useSyncExternalStore(subscribeMuted, readMuted, () => false);
  const audioRefs = useRef({});

  // Preload once per component that mounts the hook. Cheap — mp3s are <500 KB total.
  useEffect(() => {
    for (const [key, src] of Object.entries(SOUNDS)) {
      if (!audioRefs.current[key]) {
        const a = new Audio(src);
        a.preload = "auto";
        // Volumes halved 2026-09-19 per user request (sounds felt too loud).
        // Lab-end sounds slightly louder than command taps to feel celebratory
        // without startling headphone users.
        a.volume = key.startsWith("lab") ? 0.425 : 0.225;
        audioRefs.current[key] = a;
      }
    }
  }, []);

  const play = useCallback((key) => {
    if (muted) return;
    const a = audioRefs.current[key];
    if (!a) return;
    try {
      a.currentTime = 0;
      const p = a.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
      // The lab-right celebration file is ~10s; user only wants 5s. Cap
      // playback with a timer that pauses cleanly at 5000 ms. Other clips
      // are naturally short so no cap needed.
      if (key === "labRight") {
        setTimeout(() => {
          try {
            if (!a.paused) {
              a.pause();
              a.currentTime = 0;
            }
          } catch { /* no-op */ }
        }, 5000);
      }
    } catch {
      /* browsers block autoplay on some paths — silent no-op */
    }
  }, [muted]);

  return {
    muted,
    toggleMuted: useCallback(() => writeMuted(!readMuted()), []),
    setMuted:    useCallback((v) => writeMuted(!!v), []),
    playCmdRight: useCallback(() => play("cmdRight"), [play]),
    playCmdWrong: useCallback(() => play("cmdWrong"), [play]),
    playLabRight: useCallback(() => play("labRight"), [play]),
    playLabWrong: useCallback(() => play("labWrong"), [play]),
  };
}
